import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";
import { Container, ListGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Flag from "react-world-flags";
import AttendeeMatchButton from "../components/AttendeeMatchButton";
import MatchConfirmModal from "../modals/matchConfirmModal";
import Spinner from "../components/Spinner";
import { db } from "../firebase";
import useMatches from "../hooks/useMatches";
import MatchStatus from "../components/matches/MatchStatus";

export default function MyMatches({ user }) {
	const [allUsers, setAllUsers] = useState([]);
	const [loadingUsers, setLoadingUsers] = useState(true);
	const [matchTarget, setMatchTarget] = useState(null);
	const [matching, setMatching] = useState(false);
	const navigate = useNavigate();

	const {
		iHaveMatchedThem,
		theyHaveMatchedMe,
		isMutualWith,
		match,
		unmatch,
		loading: loadingMatches,
	} = useMatches(user.uid);

	const fetchAllRelevantUsers = useCallback(async () => {
		try {
			// People I matched
			const myRef = doc(db, "users", user.uid);
			const mySnap = await getDoc(myRef);
			const iMatchedUids = mySnap.data()?.matches || [];

			// People who matched me
			const theyMatchedSnap = await getDocs(
				query(collection(db, "users"), where("matches", "array-contains", user.uid)),
			);
			const theyMatchedUids = theyMatchedSnap.docs.map((d) => d.id);

			// Merge + dedupe
			const allUids = [...new Set([...iMatchedUids, ...theyMatchedUids])];

			if (allUids.length === 0) {
				setAllUsers([]);
				return;
			}

			const userPromises = allUids.map(async (uid) => {
				const ref = doc(db, "users", uid);
				const snap = await getDoc(ref);
				return snap.exists() ? { uid, ...snap.data() } : null;
			});

			const users = await Promise.all(userPromises);
			setAllUsers(users.filter(Boolean));
		} catch (error) {
			console.error("Error fetching match users:", error);
		} finally {
			setLoadingUsers(false);
		}
	}, [user.uid]);

	useEffect(() => {
		fetchAllRelevantUsers();
	}, [fetchAllRelevantUsers]);

	const handleConfirmMatch = async () => {
		if (!matchTarget) return;
		setMatching(true);
		await match(matchTarget.uid);
		setMatching(false);
		setMatchTarget(null);
	};

	const handleUnmatch = async (uid) => {
		await unmatch(uid);
		if (!theyHaveMatchedMe(uid)) {
			setAllUsers((prev) => prev.filter((u) => u.uid !== uid));
		}
	};

	if (loadingUsers || loadingMatches) return <Spinner />;

	return (
		<Container>
			<h3 className="mb-3 mt-3">My Matches ({allUsers.length})</h3>

			{allUsers.length === 0 ? (
				<p className="text-muted">No matches yet — start connecting at events!</p>
			) : (
				<ListGroup variant="flush">
					{allUsers.map((person) => {
						const isCurrentUser = person.uid === user.uid;
						const iHaveMatched = iHaveMatchedThem(person.uid);
						const theyMatched = theyHaveMatchedMe(person.uid);
						const isMutual = isMutualWith(person.uid);

						return (
							<ListGroup.Item
								key={person.uid}
								className="d-flex py-2 p-0"
								style={{ cursor: "pointer" }}
								onClick={(e) => {
									e.stopPropagation();
									e.preventDefault();
									navigate(`/profile/${person.uid}`);
								}}
							>
								{/* Col 1: Avatar - fixed width */}
								<img
									src={person.photoURL || "/images/no-profile-picture.png"}
									alt={person.displayName || "User"}
									className="rounded-circle me-3 flex-shrink-0"
									style={{ width: "50px", height: "50px", objectFit: "cover" }}
								/>
								{/* Col 2: Text - takes remaining space, truncates */}
								<div className="flex-grow-1 overflow-hidden me-2">
									<div className="fw-bold text-truncate">
										<div className="fw-bold">
											{person?.nationality && (
												<Flag
													code={person.nationality.code}
													style={{ width: "30px", height: "20px", marginRight: "8px" }}
												/>
											)}
											{person.displayName}
										</div>
									</div>
									<small className="text-muted text-truncate mt-1" style={{ lineHeight: "1.2", display: "block" }}>
										{person.interestText || ""}
										{person.interestText?.length > 50
											? person.interestText.slice(0, 50) + "..."
											: person.interestText || ""}
									</small>

									<MatchStatus
										displayName={person.displayName}
										iHaveMatched={iHaveMatched}
										theyMatched={theyMatched}
										isMutual={isMutual}
										isCurrentUser={isCurrentUser}
									/>
								</div>

								{/* Col 3: Button - fixed width, never shrinks */}
								<div
									onClick={(e) => {
										e.stopPropagation();
										e.preventDefault();
									}}
									className="flex-shrink-0 align-self-center"
									style={{ minWidth: "90px", textAlign: "right" }}
								>
									<AttendeeMatchButton
										iHaveMatchedThem={iHaveMatched}
										isMutual={isMutual}
										onMatch={() => setMatchTarget({ uid: person.uid, displayName: person.displayName })}
										onUnmatch={() => handleUnmatch(person.uid)}
									/>
								</div>
							</ListGroup.Item>
						);
					})}
				</ListGroup>
			)}

			<MatchConfirmModal
				matchTarget={matchTarget}
				onConfirm={handleConfirmMatch}
				onCancel={() => setMatchTarget(null)}
				matching={matching}
			/>
		</Container>
	);
}
